import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { Organization, CreateOrganizationRequest, UpdateOrganizationRequest } from '../../api/organizations';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { Card } from '../ui/Card/Card';
import styles from './OrganizationForm.module.css';

import { logger } from '../../utils/logger';

// Validation schema. `slug` and `plan_type` are loose (`z.string()`) because:
// 1. They are not rendered in edit mode, so a zod error on them is invisible
//    to the user and silently blocks `handleSubmit` — the exact symptom we
//    hit when an org had `plan_type='pro'` from the backend taxonomy that
//    does not match the FE enum. Stricter rules would reject live data.
// 2. In create mode `plan_type` is fixed to 'free' below (no picker is
//    rendered - the creation endpoint 422s anything else per spec 20 §5), so
//    a strict enum here would only provide false-security over a value the
//    user never actually chooses.
const organizationSchema = z.object({
  name: z.string()
    .min(1, 'Organization name is required')
    .min(2, 'Organization name must be at least 2 characters')
    .max(100, 'Organization name must be less than 100 characters'),
  slug: z.string(),
  billing_email: z.string()
    .min(1, 'Billing email is required')
    .email('Please enter a valid email address'),
  plan_type: z.string(),
  feature_flags: z.object({
    advanced_analytics: z.boolean().optional(),
    priority_support: z.boolean().optional(),
    custom_branding: z.boolean().optional(),
    api_access: z.boolean().optional(),
  }).optional(),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

interface OrganizationFormProps {
  organization?: Organization;
  onSubmit: (data: CreateOrganizationRequest | UpdateOrganizationRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  mode?: 'create' | 'edit';
  error?: string | null;
}

export const OrganizationForm: React.FC<OrganizationFormProps> = ({
  organization,
  onSubmit,
  onCancel,
  isLoading = false,
  mode = 'create',
  error = null,
}) => {
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    reset,
  } = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: organization?.name || '',
      slug: organization?.slug || '',
      billing_email: organization?.billing_email || '',
      plan_type: organization?.plan_type || 'free',
      feature_flags: {
        advanced_analytics: organization?.settings?.feature_flags?.advanced_analytics || false,
        priority_support: organization?.settings?.feature_flags?.priority_support || false,
        custom_branding: organization?.settings?.feature_flags?.custom_branding || false,
        api_access: organization?.settings?.feature_flags?.api_access || false,
      },
    },
  });

  const watchedName = watch('name');
  const watchedPlanType = watch('plan_type');

  // Auto-generate slug from name if not manually edited
  useEffect(() => {
    if (!slugManuallyEdited && watchedName && mode === 'create') {
      const generatedSlug = watchedName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .slice(0, 50);
      setValue('slug', generatedSlug);
    }
  }, [watchedName, slugManuallyEdited, setValue, mode]);

  const handleFormSubmit = async (data: OrganizationFormData) => {
    setIsSubmitting(true);
    try {
      const submitData = mode === 'create' 
        ? {
            name: data.name,
            slug: data.slug,
            billing_email: data.billing_email,
            plan_type: data.plan_type,
          } as CreateOrganizationRequest
        : {
            name: data.name,
            billing_email: data.billing_email,
            settings: {
              feature_flags: data.feature_flags,
            },
          } as UpdateOrganizationRequest;

      await onSubmit(submitData);
      if (mode === 'create') {
        reset();
      }
    } catch (error) {
      logger.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAvailableFeatures = (planType: string) => {
    switch (planType) {
      case 'enterprise':
        return ['advanced_analytics', 'priority_support', 'custom_branding', 'api_access'];
      case 'professional':
        return ['advanced_analytics', 'priority_support', 'api_access'];
      case 'free':
      default:
        return [];
    }
  };

  const availableFeatures = getAvailableFeatures(watchedPlanType);

  const featureDescriptions = {
    advanced_analytics: 'Advanced analytics and reporting capabilities',
    priority_support: '24/7 priority customer support',
    custom_branding: 'Custom branding and white-label options',
    api_access: 'Full API access and integrations',
  };

  return (
    <Card className={styles.organizationForm}>
      <div className={styles.formHeader}>
        <h2 className={styles.formTitle}>
          {mode === 'create' ? 'Create Organization' : 'Edit Organization'}
        </h2>
        <p className={styles.formDescription}>
          {mode === 'create' 
            ? 'Set up a new organization for your team.'
            : 'Update your organization settings and preferences.'
          }
        </p>
      </div>

      <form
        onSubmit={handleSubmit(handleFormSubmit, (formErrors) => {
          // Surface validation errors to the console so a click that "does
          // nothing" is never invisible. Errors on fields not rendered in the
          // current mode (e.g. slug/plan_type in edit mode) would otherwise
          // silently block submission.
          logger.error('OrganizationForm validation errors:', formErrors);
        })}
        className={styles.form}
      >
        {/* Error Message */}
        {error && (
          <div className={styles.errorAlert}>
            <span className={styles.errorIcon}>⚠️</span>
            <span className={styles.errorMessage}>{error}</span>
          </div>
        )}

        {/* Basic Information */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Basic Information</h3>
          
          <div className={styles.fieldGroup}>
            <Input
              label="Organization Name"
              type="text"
              {...register('name')}
              error={errors.name?.message}
              placeholder="Enter organization name"
              required
            />
            
            {mode === 'create' && (
              <Input
                label="Organization Slug"
                type="text"
                {...register('slug')}
                error={errors.slug?.message}
                placeholder="organization-slug"
                required
                onChange={(e) => {
                  setSlugManuallyEdited(true);
                  register('slug').onChange(e);
                }}
                helperText="This will be used in URLs and cannot be changed later"
              />
            )}
            
            <Input
              label="Billing Email"
              type="email"
              {...register('billing_email')}
              error={errors.billing_email?.message}
              placeholder="billing@example.com"
              required
              helperText="Where we'll send invoices and billing notifications"
            />
          </div>
        </div>

        {/* Plan Type - creation is restricted to the Free plan (spec 20 §5).
            Paid-org creation needs a checkout flow that belongs to billing
            and is out of scope here, so this is a static notice rather than
            a picker with only one real choice. plan_type stays 'free' via
            defaultValues below with nothing rendered to change it. */}
        {mode === 'create' && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Plan Type</h3>
            <p className={styles.sectionDescription}>
              New organizations are created on the Free plan. Paid plans are
              managed from Billing after the organization exists.
            </p>
          </div>
        )}

        {/* Feature Flags */}
        {mode === 'edit' && availableFeatures.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Features</h3>
            <p className={styles.sectionDescription}>
              Enable or disable features for your organization.
            </p>
            
            <div className={styles.featureFlags}>
              {availableFeatures.map((feature) => (
                <label key={feature} className={styles.featureFlag}>
                  <input
                    type="checkbox"
                    {...register(`feature_flags.${feature}` as any)}
                    className={styles.featureCheckbox}
                  />
                  <div className={styles.featureInfo}>
                    <span className={styles.featureName}>
                      {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <span className={styles.featureDescription}>
                      {featureDescriptions[feature as keyof typeof featureDescriptions]}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting || isLoading}
            disabled={!isDirty && mode === 'edit'}
          >
            {mode === 'create' ? 'Create Organization' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Card>
  );
}; 